// Dynamic Data Service for Admin Panel
class DataService {
    constructor() {
        this.listeners = {};
        this.data = this.generateInitialData();
        this.startRealTimeUpdates();
    }

    // Generate initial data
    generateInitialData() {
        return {
            stats: this.generateStats(),
            visitorData: this.generateVisitorData(),
            users: this.generateUsers(),
            contents: this.generateContents(),
            activityWorkers: this.generateActivityWorkers()
        };
    }

    // Generate dynamic stats
    generateStats() {
        return [
            {
                id: 'views',
                label: 'Total Views',
                value: Math.floor(Math.random() * 10000) + 5000,
                icon: 'IoEyeOutline',
                trend: Math.random() > 0.5 ? 'up' : 'down',
                change: Math.floor(Math.random() * 20) + 5
            },
            {
                id: 'scans',
                label: 'Total Scans Triggered',
                value: Math.floor(Math.random() * 5000) + 1000,
                icon: 'TbLayoutDashboard',
                trend: Math.random() > 0.5 ? 'up' : 'down',
                change: Math.floor(Math.random() * 15) + 3
            },
            {
                id: 'users',
                label: 'New Users',
                value: Math.floor(Math.random() * 1000) + 100,
                icon: 'ImUsers',
                trend: Math.random() > 0.5 ? 'up' : 'down',
                change: Math.floor(Math.random() * 25) + 10
            }
        ];
    }

    // Generate visitor analytics data
    generateVisitorData() {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return days.map(day => ({
            day,
            black: Math.floor(Math.random() * 80) + 20, // Last week
            white: Math.floor(Math.random() * 80) + 20  // This week
        }));
    }

    // Generate users data
    generateUsers() {
        const firstNames = ['John', 'Alice', 'Bob', 'Emma', 'Michael', 'Sarah', 'David', 'Lisa', 'James', 'Maria'];
        const lastNames = ['Doe', 'Smith', 'Johnson', 'Wilson', 'Brown', 'Davis', 'Miller', 'Garcia', 'Rodriguez', 'Martinez'];
        const roles = ['Admin', 'Editor', 'Viewer', 'Moderator'];
        const statuses = ['Active', 'Inactive'];

        const users = [];
        const numUsers = Math.floor(Math.random() * 15) + 5; // 5-20 users

        for (let i = 1; i <= numUsers; i++) {
            const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
            const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
            const name = `${firstName} ${lastName}`;
            const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;

            users.push({
                id: i,
                name,
                role: roles[Math.floor(Math.random() * roles.length)],
                status: statuses[Math.floor(Math.random() * statuses.length)],
                email,
                lastLogin: this.getRandomDate(),
                avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=ff2d2d&color=fff&size=40`
            });
        }

        return users;
    }

    // Generate content data
    generateContents() {
        const titles = [
            'Getting Started with Dashboard',
            'Quarterly Report Q4',
            'New Features Overview',
            'User Guidelines 2025',
            'API Documentation',
            'Security Best Practices',
            'Performance Optimization',
            'Mobile App Update',
            'Data Analytics Guide',
            'Customer Success Stories'
        ];

        const types = ['Article', 'Report', 'Video', 'PDF', 'Guide', 'Tutorial'];
        const authors = ['John Doe', 'Alice Smith', 'Emma Wilson', 'Bob Johnson', 'Sarah Davis', 'Michael Brown'];
        const statuses = ['Published', 'Draft', 'Review', 'Archived'];

        const contents = [];
        const numContents = Math.floor(Math.random() * 12) + 4; // 4-16 contents

        for (let i = 1; i <= numContents; i++) {
            contents.push({
                id: i,
                title: titles[Math.floor(Math.random() * titles.length)],
                type: types[Math.floor(Math.random() * types.length)],
                author: authors[Math.floor(Math.random() * authors.length)],
                date: this.getRandomDate(),
                status: statuses[Math.floor(Math.random() * statuses.length)],
                views: Math.floor(Math.random() * 10000),
                likes: Math.floor(Math.random() * 500)
            });
        }

        return contents;
    }

    // Generate activity workers data
    generateActivityWorkers() {
        return {
            current: Math.floor(Math.random() * 800) + 200, // 200-1000
            max: 1000,
            percentage: Math.floor(Math.random() * 100)
        };
    }

    // Helper to generate random dates
    getRandomDate() {
        const start = new Date(2024, 0, 1);
        const end = new Date();
        const randomDate = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
        return randomDate.toISOString().split('T')[0];
    }

    // Get data by type
    getData(type) {
        return new Promise((resolve) => {
            // Simulate API delay
            setTimeout(() => {
                resolve(this.data[type]);
            }, Math.random() * 500 + 200); // 200-700ms delay
        });
    }

    // Update specific data type
    updateData(type, newData) {
        this.data[type] = newData;
        this.notifyListeners(type);
    }

    // Subscribe to data changes
    subscribe(type, callback) {
        if (!this.listeners[type]) {
            this.listeners[type] = [];
        }
        this.listeners[type].push(callback);

        // Return unsubscribe function
        return () => {
            this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
        };
    }

    // Notify listeners of data changes
    notifyListeners(type) {
        if (this.listeners[type]) {
            this.listeners[type].forEach(callback => callback(this.data[type]));
        }
    }

    // Start real-time updates
    startRealTimeUpdates() {
        // Update stats every 30 seconds
        setInterval(() => {
            this.updateData('stats', this.generateStats());
        }, 30000);

        // Update visitor data every 60 seconds
        setInterval(() => {
            this.updateData('visitorData', this.generateVisitorData());
        }, 60000);

        // Update activity workers every 45 seconds
        setInterval(() => {
            this.updateData('activityWorkers', this.generateActivityWorkers());
        }, 45000);

        // Occasionally update users (simulate new registrations)
        setInterval(() => {
            if (Math.random() > 0.7) { // 30% chance every 2 minutes
                this.updateData('users', this.generateUsers());
            }
        }, 120000);

        // Occasionally update contents (simulate new content)
        setInterval(() => {
            if (Math.random() > 0.8) { // 20% chance every 3 minutes
                this.updateData('contents', this.generateContents());
            }
        }, 180000);
    }

    // CRUD operations for users
    addUser(userData) {
        const newUser = {
            id: Math.max(...this.data.users.map(u => u.id)) + 1,
            ...userData,
            lastLogin: this.getRandomDate(),
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name)}&background=ff2d2d&color=fff&size=40`
        };
        this.data.users.push(newUser);
        this.notifyListeners('users');
        return newUser;
    }

    updateUser(id, updates) {
        const index = this.data.users.findIndex(u => u.id === id);
        if (index !== -1) {
            this.data.users[index] = { ...this.data.users[index], ...updates };
            this.notifyListeners('users');
            return this.data.users[index];
        }
        return null;
    }

    deleteUser(id) {
        this.data.users = this.data.users.filter(u => u.id !== id);
        this.notifyListeners('users');
    }

    // CRUD operations for contents
    addContent(contentData) {
        const newContent = {
            id: Math.max(...this.data.contents.map(c => c.id)) + 1,
            ...contentData,
            date: new Date().toISOString().split('T')[0],
            views: 0,
            likes: 0
        };
        this.data.contents.push(newContent);
        this.notifyListeners('contents');
        return newContent;
    }

    updateContent(id, updates) {
        const index = this.data.contents.findIndex(c => c.id === id);
        if (index !== -1) {
            this.data.contents[index] = { ...this.data.contents[index], ...updates };
            this.notifyListeners('contents');
            return this.data.contents[index];
        }
        return null;
    }

    deleteContent(id) {
        this.data.contents = this.data.contents.filter(c => c.id !== id);
        this.notifyListeners('contents');
    }
}

// Create singleton instance
const dataService = new DataService();

export default dataService;
